'use client';

import classnames from 'classnames';
import {
  Form,
  Formik,
  type FormikErrors,
  type FormikValues,
  validateYupSchema,
  yupToFormErrors,
} from 'formik';
import { FcGoogle } from 'react-icons/fc';
import * as Yup from 'yup';

import { Button } from '../../button';
import { FormFeedback, FormGroup, Input } from '../../forms';
import { Logo } from '../../logo';
import type { SignInProps } from './SignIn.types';

import styles from './SignIn.module.scss';

interface FormValues {
  email: string;
  password: string;
}

function SignIn({
  style,
  className,
  labels,
  onLoginWithGoogle,
  onSignInWithEmailAndPassword,
  onResetPassword,
  onRegister,
  testId = 'ui-sign-in',
}: SignInProps) {
  return (
    <div
      style={style}
      className={classnames(styles['sign-in'], className)}
      data-testid={testId}
    >
      <Formik
        enableReinitialize
        validate={async (values: FormValues) => {
          const errors: FormikErrors<FormValues> = {};

          const validationSchema = Yup.object().shape({
            email: Yup.string()
              .required(
                labels?.email?.validation?.required ??
                  'Tu email no puede estar vacío.',
              )
              .email(
                labels?.email?.validation?.format ??
                  'El formato del email introducido no es válido.',
              ),
            password: Yup.string().required(
              labels?.password?.validation?.required ??
                'La contraseña no puede estar vacía.',
            ),
          });

          try {
            validateYupSchema<FormValues>(values, validationSchema, true);
          } catch (err) {
            return yupToFormErrors(err);
          }

          return errors;
        }}
        onSubmit={async (values, { setSubmitting }) => {
          setSubmitting(true);

          onSignInWithEmailAndPassword(values.email, values.password);

          setSubmitting(false);
        }}
        initialValues={{
          email: '',
          password: '',
        }}
      >
        {({
          values,
          touched,
          errors,
          isSubmitting,
          handleChange,
          handleSubmit,
        }: FormikValues) => (
          <>
            <div className={styles['header']}>
              <Logo variant="logo-h" className={styles['logo']} />
            </div>

            <div>
              <Form noValidate onSubmit={handleSubmit}>
                <FormGroup className={styles['form-group']}>
                  <Input
                    id="email"
                    name="email"
                    value={values.email}
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    aria-label={labels?.email?.placeholder ?? 'Email'}
                    placeholder={labels?.email?.placeholder ?? 'Email'}
                    onChange={handleChange}
                    error={touched.email && !!errors.email}
                    aria-describedby={
                      touched.email && errors.email ? 'email-error' : undefined
                    }
                  />
                  {touched.email && errors.email && (
                    <FormFeedback id="email-error" variant="danger">
                      {errors.email}
                    </FormFeedback>
                  )}
                </FormGroup>
                <FormGroup className={styles['form-group']}>
                  <Input
                    id="password"
                    name="password"
                    value={values.password}
                    type="password"
                    autoComplete="current-password"
                    spellCheck={false}
                    aria-label={labels?.password?.placeholder ?? 'Contraseña'}
                    placeholder={labels?.password?.placeholder ?? 'Contraseña'}
                    onChange={handleChange}
                    error={touched.password && !!errors.password}
                    aria-describedby={
                      touched.password && errors.password
                        ? 'password-error'
                        : undefined
                    }
                  />
                  {touched.password && errors.password && (
                    <FormFeedback id="password-error" variant="danger">
                      {errors.password}
                    </FormFeedback>
                  )}
                  <FormFeedback
                    className={styles['reset-password']}
                    onClick={onResetPassword}
                  >
                    {labels?.forgot_password?.placeholder ??
                      '¿Has olvidado tu contraseña?'}
                  </FormFeedback>
                </FormGroup>
                <Button
                  className={styles['submit-button']}
                  type="submit"
                  disabled={isSubmitting}
                >
                  {labels?.login?.password ?? 'Iniciar sesión'}
                </Button>
              </Form>
              <div className={styles['or']}>
                <hr />
                <p>{labels?.or ?? 'o'}</p>
                <hr />
              </div>
              <Button
                className={styles['google-button']}
                variant="outline-primary"
                prefixIcon={<FcGoogle />}
                disabled={isSubmitting}
                onClick={onLoginWithGoogle}
              >
                {labels?.login?.google ?? 'Iniciar sesión con Google'}
              </Button>
            </div>

            <div className={styles['not-have-account']}>
              <span className={styles['not-have-account__text']}>
                {labels?.not_registered ?? '¿No tienes cuenta?'}
              </span>
              <div
                className={styles['not-have-account__link']}
                onClick={onRegister}
              >
                <strong>{labels?.register ?? 'Regístrate'}</strong>
              </div>
            </div>
          </>
        )}
      </Formik>
    </div>
  );
}

export { SignIn };
